import LazyLoadImage from '@repo/ui/src/components/LazyLoadImage';
import { ToutContent } from '@repo/ui/src/components/ToutContent';
import { HorizontalAlignmentEnum, VerticalAlignmentEnum } from '@repo/ui/src/enums/shared-enums';
import { imageFullWidthXs } from '@repo/ui/src/styles/variables';
import { classNames } from '@repo/ui/src/utils/classNames';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ImageProps, ToutContentProps } from '@repo/ui/src/types';
import Image from '@repo/ui/src/components/Image/New';
import { appConfig } from '../../utils/appConfig';

const bgClasses = {
  'Light Gray': 'bg-warm-200',
  'Light Gray with Log': 'bg-warm-200',
  Dark: 'bg-neutral-700',
};

export const verticalAlignmentClasses = {
  [VerticalAlignmentEnum.Top]: 'items-start',
  [VerticalAlignmentEnum.Center]: 'items-center',
  [VerticalAlignmentEnum.Bottom]: 'items-end',
};
export const horizontalAlignmentClasses = {
  [HorizontalAlignmentEnum.Left]: 'justify-start',
  [HorizontalAlignmentEnum.Center]: 'justify-center',
  [HorizontalAlignmentEnum.Right]: 'justify-start',
};

interface SplitCardProps {
  toutContent: ToutContentProps;
  image: ImageProps;
  horizontalAlignment?: HorizontalAlignmentEnum;
  verticalAlignment?: VerticalAlignmentEnum;
  hasUnlimitedChars?: boolean;
  logBackground: boolean;
  splitLayoutBackground: keyof typeof bgClasses;
  _testID?: string;
  index?: number;
}

const SplitCard = ({
  toutContent,
  image,
  horizontalAlignment,
  verticalAlignment,
  hasUnlimitedChars,
  logBackground,
  splitLayoutBackground,
  _testID,
  index,
}: SplitCardProps) => {
  const logBackgroundImageUrl =
    'https://traegergrills.a.bigcontent.io/v1/static/log-cropped-transparent';

  const imageUrl = buildImageSrc(image, imageFullWidthXs, 1, 1);
  return (
    <div data-test-id={`split_card_${_testID}${index}`} className="relative">
      <div className={classNames('grid auto-rows-fr md:grid-cols-2 h-full')}>
        {image?.image && (
          <div
            className={classNames(
              'flex content-center items-center flex-1 h-auto relative',
              horizontalAlignment === 'left' && 'md:order-1',
            )}
          >
            <Image src={imageUrl} alt="" className="w-full h-full object-cover" fill />
          </div>
        )}
        <div
          className={classNames(
            'flex flex-1 h-auto px-0',
            bgClasses[splitLayoutBackground],
            horizontalAlignment === 'right' && 'md:pl-gutterSM',
            horizontalAlignment !== 'right' && 'md:pr-gutterSM',
            horizontalAlignment
              ? horizontalAlignmentClasses[horizontalAlignment]
              : horizontalAlignmentClasses[HorizontalAlignmentEnum.Left],
            verticalAlignment
              ? verticalAlignmentClasses[verticalAlignment]
              : verticalAlignmentClasses[VerticalAlignmentEnum.Center],
          )}
        >
          {logBackground && (
            <LazyLoadImage
              imgComponent={
                <img
                  src={logBackgroundImageUrl}
                  alt=""
                  role="presentation"
                  className={classNames(
                    'absolute bottom-0 w-[75%] inset-x-[15%] z-0 sm:w-[35%] sm:left-[8%] sm:right-0 md:w-[30%] md:left-[10%]',
                    !splitLayoutBackground && 'px-0',
                  )}
                />
              }
            />
          )}
          <ToutContent
            divider={!!toutContent.description}
            lineClamp={hasUnlimitedChars ? 4 : null}
            buttonNegative={splitLayoutBackground === 'Dark'}
            {...toutContent}
            className={classNames(
              'max-w-full z-10 md:line-clamp-4 lg:max-w-[80%] md:[&_p]:line-clamp-4',
              !splitLayoutBackground && 'px-0',
              appConfig.isTraeger && 'p-6 md:p-gutterLG lg:p-gutterSM',
              appConfig.isMeater && 'px-20 py-28',
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default SplitCard;
